import type { GetOrganizationMetadataQuery } from '@pypestream/api-services/urql';

/**
 * Per-org `canManageAccess` resolution (PSTUDIO-3111).
 *
 * The backend is the source of truth for authorization: each org's
 * `canManageAccess` comes from `organizationMetadata(accountId).canManageAccess`.
 * These helpers only normalise and aggregate those backend responses — they do
 * NOT compute permissions on the client. Kept dependency-light (type-only import)
 * so unit tests do not pull in the design-system runtime.
 */
export type OrganizationMetadataResult = {
  accountId: string;
  canManageAccess: boolean;
};

export const toOrganizationMetadataResult = (
  accountId: string,
  data: GetOrganizationMetadataQuery | undefined
): OrganizationMetadataResult => ({
  accountId,
  canManageAccess: !!data?.admin_?.organizationMetadata?.canManageAccess,
});

export const buildCanManageAccessByOrgId = (
  results: OrganizationMetadataResult[]
): Record<string, boolean> =>
  results.reduce<Record<string, boolean>>(
    (acc, { accountId, canManageAccess }) => {
      if (accountId) {
        acc[accountId] = canManageAccess;
      }
      return acc;
    },
    {}
  );

// NOTE: one organizationMetadata query is issued per org (N-query fan-out),
// because the backend exposes canManageAccess only per single accountId. For
// users in many child orgs this is N parallel requests per page load; a
// batched backend field (organizationMetadata(accountIds: [...])) would reduce
// it to one request and is the right follow-up. Failures are isolated below.
// `known` seeds flags already fetched elsewhere (e.g. the route org's
// organizationMetadata returned by GetAccountDetails) so they are not
// re-queried.
export const fetchCanManageAccessByOrgId = async (
  orgs: { id?: string | null }[],
  getOrganizationMetadata: (vars: {
    accountId: string;
  }) => PromiseLike<{ data?: GetOrganizationMetadataQuery }>,
  known: Record<string, boolean> = {}
): Promise<Record<string, boolean>> => {
  const accountIds = orgs
    .map((org) => org.id)
    .filter((id): id is string => !!id && !(id in known));

  // Promise.allSettled (not all): a single failed org-metadata query must not
  // break the whole page. A rejected lookup falls back to canManageAccess:false
  // (fail-closed — deny, never grant, on error).
  const settled = await Promise.allSettled(
    accountIds.map((accountId) =>
      getOrganizationMetadata({ accountId }).then((res) =>
        toOrganizationMetadataResult(accountId, res.data)
      )
    )
  );

  const results = settled.map((outcome, index) =>
    outcome.status === 'fulfilled'
      ? outcome.value
      : { accountId: accountIds[index], canManageAccess: false }
  );

  return { ...known, ...buildCanManageAccessByOrgId(results) };
};

/**
 * Per-org authorization lookup used by the user-details mutation guards.
 * Returns the backend `canManageAccess` for a single org from the rows already
 * resolved into the viewed user's state. Fail-closed: unknown org / missing
 * flag → false. Pure (no design-system import) so it is unit-testable.
 */
export const getOrgCanManageAccess = (
  orgs:
    | ReadonlyArray<{ id?: string | null; canManageAccess?: boolean }>
    | undefined,
  orgId: string | null | undefined
): boolean =>
  !!orgId && !!orgs?.find((org) => org.id === orgId)?.canManageAccess;

/** Minimal structural shape of a permissions-table org row needed to map a
 * project id to its org (assignedProjects carry the project nested, available
 * projects are the raw account project rows). */
type OrgProjectAccessRow = {
  id?: string | null;
  canManageAccess?: boolean;
  assignedProjects?: ReadonlyArray<{
    project?: { id?: string | null } | null;
  } | null> | null;
  availableProjects?: ReadonlyArray<{ id?: string | null } | null> | null;
};

const orgContainsProject = (
  org: OrgProjectAccessRow,
  projectId: string
): boolean =>
  !!org.assignedProjects?.some((p) => p?.project?.id === projectId) ||
  !!org.availableProjects?.some((p) => p?.id === projectId);

/**
 * Per-org gate for persisting project-role updates: keeps only the entries
 * whose project belongs to an org the logged-in user can manage. Needed
 * because updates can span orgs (per-org guards allow child-org edits, and
 * the consolidation path generates entries without going through the guards)
 * while `setUserAccountProjectsRoles` was previously all-or-nothing on the
 * route org's flag. Fail-closed: entries whose project cannot be mapped to a
 * manageable org are dropped.
 */
export const filterProjectRolesUpdateByOrgAccess = <
  T extends { id?: string | null },
>(
  orgs: ReadonlyArray<OrgProjectAccessRow> | undefined,
  projectRolesUpdate: ReadonlyArray<T> | undefined
): T[] =>
  (projectRolesUpdate || []).filter(
    (update) =>
      !!update.id &&
      !!orgs?.find((org) => orgContainsProject(org, update.id!))
        ?.canManageAccess
  );
